import InvoicePurchaseFlow from './InvoicePurchaseFlow';
import InvoicePZNumbers from './InvoicePZNumbers';
import InvoiceReceiptCostReview, { type ReceiptCostEdit } from './InvoiceReceiptCostReview';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, App, Button, Card, Collapse, DatePicker, Popconfirm, Space, Spin, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, FileAddOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import WorkflowInvoiceEditor from './WorkflowInvoiceEditor';
import InvoiceDraftSummary from './InvoiceDraftSummary';
import InvoiceEditorModal from './InvoiceEditorModal';
import InvoiceOrderPicker from './InvoiceOrderPicker';
import InvoiceApprovalPreview from './InvoiceApprovalPreview';
import { invoiceWorkflowApi, fetchInvoiceApprovalPreview, type InvoiceApprovalPreview as ApprovalPreview, type InvoiceApprovalInput, type InvoiceApprovalResult } from '../api/invoiceWorkflowApi';
import { getInvoiceImportErrorMessage, isBlockingInvoiceImportError } from '../invoiceImportErrorMessages';
import { mergeInvoiceSourceMetadata } from '../invoiceDraftState';
import { validateWorkflowInvoiceForApproval } from '../workflowInvoiceValidation';
import type { WorkflowInvoice, WorkflowInvoiceDirection, WorkflowInvoiceSourceLineCandidate } from '../../../types/workflowInvoice';
import { useFeedback } from '../../../utils/feedback';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';
import './InvoiceDraftDetail.css';
import './InvoiceDraftSummary.css';
import './InvoiceEditorModal.css';

interface InvoiceDraftDetailProps {
  direction: WorkflowInvoiceDirection;
  embedded?: {
    invoiceId: string;
    supplierOrderId: string;
    onClose: () => void;
    onPersisted: () => void;
  };
}

export default function InvoiceDraftDetail({ direction, embedded }: InvoiceDraftDetailProps) {
  const purchaseFlow = direction === 'PURCHASE';
  const { id: routeId } = useParams<{ id: string }>();
  const [embeddedId, setEmbeddedId] = useState(embedded?.invoiceId);
  const id = embedded ? embeddedId : routeId;
  const navigate = useNavigate();
  const { t } = useTranslation(['invoices', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const commands = useMemo(() => invoiceWorkflowApi(direction), [direction]);
  const base = direction === 'PURCHASE' ? '/app/purchase-invoices' : '/app/sales-invoices';
  const isNew = id === 'new';
  const [draft, setDraft] = useState<WorkflowInvoice | null>(null);
  const current = useRef<WorkflowInvoice | null>(null);
  const revision = useRef(0);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [candidates, setCandidates] = useState<WorkflowInvoiceSourceLineCandidate[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<ApprovalPreview | null>(null);
  const [previewRevision, setPreviewRevision] = useState(0);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [pendingSelections, setPendingSelections] = useState<NonNullable<InvoiceApprovalInput['pendingStockSelections']>>([]);
  const [receiptCosts, setReceiptCosts] = useState<ReceiptCostEdit[]>([]);
  const [pzNumbers, setPZNumbers] = useState<Record<string, string>>({});
  const [approvalReviewRequired, setApprovalReviewRequired] = useState(false);
  const reviewedPreview = useRef<string | null>(null);
  const approvalEdits = useRef({ receiptCosts, pendingSelections });
  approvalEdits.current = { receiptCosts, pendingSelections };
  const [approval, setApproval] = useState<InvoiceApprovalResult | null>(null);
  // Details editor lives in a same-route modal. Closing it (button, X or ESC)
  // never discards: the page draft (and its unsaved indicator) retains every
  // local edit, which survives closing but not a browser reload.
  const [editorOpen, setEditorOpen] = useState(false);
  const [purchaseStep, setPurchaseStep] = useState(0);
  const approvalKey = useRef<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const requestGeneration = useRef(0);
  const draftSession = useRef(0);
  const previousRoute = useRef({ id, direction });
  // Route identity adoption keeps the session; leaving the component does not.
  useEffect(() => () => { draftSession.current += 1; }, []);
  const replace = (next: WorkflowInvoice) => { current.current = next; setDraft(next); };
  const edit = (next: WorkflowInvoice) => { revision.current += 1; setDirty(true); replace(next); };
  const error = (cause: any) => {
    if (cause?.response?.data?.code === 'VERSION_CONFLICT') setConflict(true);
    feedback.error(getInvoiceImportErrorMessage(cause, t, 'invoices:error.updateFailed'), { sticky: isBlockingInvoiceImportError(cause) });
  };
  // Busy flags belong to the draft session that started the request. A late
  // completion from a previous route must never clear the new draft's state.
  const finishBusy = (session: number) => { if (session === draftSession.current) setBusy(false); };
  const finishExtracting = (session: number) => { if (session === draftSession.current) setExtracting(false); };
  useEffect(() => {
    const previous = previousRoute.current;
    previousRoute.current = { id, direction };
    // Creating a draft adopts its server identity without loading over the
    // editor. This also preserves edits made while create/upload was pending.
    if (previous.id === 'new' && previous.direction === direction && id !== 'new'
      && current.current?.id === id && current.current?.direction === direction) return;
    draftSession.current += 1;
    const generation = ++requestGeneration.current;
    setLoading(true); setDirty(false); setConflict(false); setApproval(null); approvalKey.current = null; revision.current = 0;
    // A real route switch owns fresh progress state: a late previous upload
    // or save must not leave the new draft's actions disabled.
    setBusy(false); setExtracting(false);
    setEditorOpen(false);
    setReceiptCosts([]); setPendingSelections([]); setApprovalReviewRequired(false); reviewedPreview.current = null;
    setPZNumbers({});
    const initialSource = embedded?.supplierOrderId ?? new URLSearchParams(window.location.search).get(direction === 'PURCHASE' ? 'supplierOrderId' : 'orderId');
    const request = isNew ? Promise.resolve<WorkflowInvoice>({ direction, kind: 'UNKNOWN', status: 'DRAFT', extractionStatus: 'IDLE', lines: [], linesLoaded: true, manuallyCorrected: false, ...(direction === 'PURCHASE' ? { supplierOrderId: initialSource } : { orderId: initialSource }) }) : commands.fetch(id!);
    request.then(value => {
      if (generation !== requestGeneration.current) return;
      replace(value);
      setPurchaseStep(value.id ? 1 : 0);
      if (value.approvalResult) setApproval({ ...value.approvalResult, invoice: value });
      const source = direction === 'PURCHASE' ? value.supplierOrderId : value.orderId;
      setSourceIds(source ? [source] : []);
    }).catch(error).finally(() => { if (generation === requestGeneration.current) setLoading(false); });
    return () => { requestGeneration.current += 1; };
    // A route change loads once; feedback/i18n identities must not reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, direction]);

  useEffect(() => {
    if (!draft?.id) return;
    let active = true;
    setPreview(null); setPreviewFailed(false); approvalKey.current = null;
    fetchInvoiceApprovalPreview(direction, draft.id).then(result => {
      if (!active) return;
      const signature = JSON.stringify({ id: draft.id, receiptCosts: result.receiptCosts,
        receiptCostsIssue: result.receiptCostsIssue, pendingStockCandidates: result.pendingStockCandidates });
      if (reviewedPreview.current !== null && reviewedPreview.current !== signature) {
        // Persisting a draft can replace line IDs. An override must never be
        // silently reassigned to another line or reset to an unnoticed proposal.
        if (approvalEdits.current.receiptCosts.length || approvalEdits.current.pendingSelections.length) setApprovalReviewRequired(true);
        setReceiptCosts([]); setPendingSelections([]);
      }
      reviewedPreview.current = signature;
      setPreview(result);
      if (direction === 'PURCHASE') setSourceIds(previous => Array.from(new Set([...previous, ...result.orders.map(order => order.id)])));
    }).catch(() => { if (active) setPreviewFailed(true); });
    return () => { active = false; };
  }, [draft?.id, draft?.version, direction, previewRevision]);


  const save = async (): Promise<WorkflowInvoice | null> => {
    const value = current.current;
    if (!value) return null;
    // File-first OCR: drafts persist without a currency so a source file can
    // be attached and read before the operator picks one. Currency stays
    // strictly required at approval via validateWorkflowInvoiceForApproval.
    if (!value.linesLoaded) { feedback.error(t('invoices:workflow.validation.linesRequired')); return null; }
    const capturedRevision = revision.current;
    const session = draftSession.current;
    const updated = value.id
      ? await commands.update(value.id, commands.payload(value), value.version!)
      : await commands.create(commands.payload(value));
    if (session !== draftSession.current) return null;
    if (revision.current === capturedRevision) { replace({ ...updated, settlementPeriod: value.settlementPeriod }); setDirty(false); }
    else if (current.current) replace(mergeInvoiceSourceMetadata(current.current, updated));
    if (!value.id && updated.id) {
      if (embedded) setEmbeddedId(updated.id);
      else navigate(`${base}/${updated.id}`, { replace: true });
    }
    embedded?.onPersisted();
    return updated;
  };
  // Modal save closes only on a clean success: a failure, a conflict, an
  // in-flight extraction, edits made during the save, or a route switch all
  // keep the current state untouched instead of closing over it.
  const handleModalSave = async () => {
    if (conflict || extracting) return;
    const session = draftSession.current;
    const capturedRevision = revision.current;
    setBusy(true);
    try {
      const updated = await save();
      if (!updated) return;
      if (session !== draftSession.current) return;
      if (revision.current !== capturedRevision) return;
      feedback.success(t('invoices:success.updated'));
      setEditorOpen(false);
    } catch (cause) { if (session === draftSession.current) error(cause); } finally { finishBusy(session); }
  };
  const upload = async (files: File[]) => {
    if (!files.length || conflict) return;
    const session = draftSession.current;
    setBusy(true);
    try {
      let value = current.current;
      if (!value?.id) value = await save();
      if (!value?.id) return;
      for (const file of files) {
        if (session !== draftSession.current) return;
        const metadata = await commands.attach(value.id, file);
        if (session !== draftSession.current) return;
        if (current.current) replace(mergeInvoiceSourceMetadata(current.current, metadata));
      }
      if (session !== draftSession.current) return;
      feedback.success(t('invoices:workflow.page.uploaded'));
      if (purchaseFlow) { setPurchaseStep(1); await extract(); }
    } catch (cause) { if (session === draftSession.current) error(cause); } finally { finishBusy(session); }
  };
  const apply = async (overwrite: boolean) => {
    const value = current.current;
    if (!value?.id || !value.extractionSeq || !value.version || conflict) return;
    const session = draftSession.current;
    setBusy(true);
    try {
      const updated = await commands.apply(value.id, { seq: value.extractionSeq, expectedVersion: value.version, overwriteManualEdits: overwrite });
      // Never merge an apply response into a draft the operator already left.
      if (session !== draftSession.current) return;
      if (!current.current || current.current.id !== value.id) return;
      replace({ ...updated, settlementPeriod: value.settlementPeriod }); setDirty(false);
      // A successful OCR application reveals the editor for verification.
      if (purchaseFlow) setPurchaseStep(1);
      else setEditorOpen(true);
    } catch (cause) { if (session === draftSession.current) error(cause); } finally { finishBusy(session); }
  };
  const confirmApply = () => {
    if (conflict) return;
    if (dirty || current.current?.manuallyCorrected) {
      modal.confirm({ title: t('invoices:workflow.page.applyOverwriteConfirm'), content: t('invoices:workflow.page.editsRetained'), okText: t('invoices:workflow.page.applyExtraction'), cancelText: t('common:cancel'), onOk: () => { void apply(true); } });
    } else void apply(false);
  };
  const extract = async () => {
    const value = current.current;
    if (!value?.id || conflict) return;
    const session = draftSession.current;
    const capturedRevision = revision.current;
    const capturedDirty = dirty;
    setExtracting(true);
    try {
      try {
        await commands.extract(value.id, []);
      } catch (cause) {
        // The server still records the FAILED extraction: refresh status
        // metadata for this draft session only, so the UI stops showing a
        // stale status. Manual edits and lines stay intact; never autoapply
        // in the error path. A failing refresh keeps the edits and reports
        // the original error (no unhandled rejection).
        try {
          const metadata = await commands.fetch(value.id);
          if (session === draftSession.current && current.current?.id === value.id) {
            replace(mergeInvoiceSourceMetadata(current.current, metadata));
          }
        } catch {
          // Metadata refresh is best-effort only.
        }
        if (session === draftSession.current) error(cause);
        return;
      }
      const metadata = await commands.fetch(value.id);
      if (session !== draftSession.current) return;
      if (!current.current || current.current.id !== value.id) return;
      replace(mergeInvoiceSourceMetadata(current.current, metadata));
      // Only a pristine editor may accept automatic prefill. Any edit during
      // inference keeps the local value and leaves an explicit apply action.
      if (session === draftSession.current && !capturedDirty && revision.current === capturedRevision && !value.manuallyCorrected) await apply(false);
    } catch (cause) { if (session === draftSession.current) error(cause); } finally { finishExtracting(session); }
  };
  const approve = async () => {
    const value = current.current;
    if (!value?.settlementPeriod) { feedback.error(t('invoices:workflow.page.periodRequired')); return; }
    if (dirty) { feedback.error(t('invoices:workflow.page.saveBeforeApproval')); return; }
    if (!preview || previewFailed) { feedback.error(t('invoices:workflow.page.previewFailed')); return; }
    if (approvalReviewRequired) { feedback.error(t('invoices:workflow.receiptCost.reviewAgain')); return; }
    if (validateWorkflowInvoiceForApproval(value).length) { feedback.error(t('invoices:workflow.page.resolveIssues')); return; }
    if (direction === 'PURCHASE' && (preview.receiptCostsIssue || !preview.receiptCosts?.length || receiptCosts.some(row => row.totalCostPLN == null || row.totalCostPLN < 0))) {
      feedback.error(t('invoices:workflow.receiptCost.required')); return;
    }
    const acceptedCosts = preview.receiptCosts?.map(row => {
      const edited = receiptCosts.find(value => value.purchaseInvoiceItemId === row.purchaseInvoiceItemId);
      return { purchaseInvoiceItemId: row.purchaseInvoiceItemId, totalCostPLN: edited ? edited.totalCostPLN! : row.proposedTotalCostPLN, ...(edited?.reason ? { reason: edited.reason } : {}) };
    });
    const session = draftSession.current;
    setBusy(true);
    try {
      const saved = dirty || !value.id ? await save() : value;
      if (session !== draftSession.current) return;
      if (!saved?.id || !saved.version) return;
      approvalKey.current ??= `invoice-${saved.id}-${saved.version}-${Date.now()}`;
      const approvalInput: InvoiceApprovalInput = { expectedVersion: saved.version, idempotencyKey: approvalKey.current, recognitionMonth: value.settlementPeriod, ...(direction === 'PURCHASE' ? {
        pendingStockSelections: pendingSelections, receiptCosts: acceptedCosts,
        pzNumbers: (preview?.orders ?? []).filter(order => order.lines.some(line => value.lines.some(item => item.sourceOrderLineId === line.sourceLineId)) && pzNumbers[order.id]?.trim()).map(order => ({ supplierOrderId: order.id, pzNumber: pzNumbers[order.id].trim() })),
      } : {}) };
      // Receipt warnings (over-receipt, invoice dated after receipt) need an audited reason before any PZ is created.
      const draftId = saved.id;
      const result = await submitWithOperationalException(
        (payload) => commands.approve(draftId, payload as unknown as InvoiceApprovalInput),
        approvalInput as unknown as Record<string, unknown>,
        modal,
        t,
      );
      if (session !== draftSession.current) return;
      if (!result) return;
      replace(result.invoice); setApproval(result); setDirty(false);
      embedded?.onPersisted();
      window.dispatchEvent(new CustomEvent('invoice-workflow-changed', { detail: { invoiceId: saved.id, orderIds: result.orders.map(order => order.id) } }));
      feedback.success(t('invoices:workflow.page.approved'));
    } catch (cause) { if (session === draftSession.current) error(cause); } finally { finishBusy(session); }
  };
  const downloadFile = (sha256: string) => {
    const value = current.current;
    if (!value?.id) return;
    void commands.download(value.id, sha256).then(blob => { const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = draft?.sourceFiles?.find(file => file.sha256 === sha256)?.name ?? 'faktura'; link.click(); URL.revokeObjectURL(url); }).catch(error);
  };
  const close = () => {
    if (busy || extracting) return;
    const leave = () => { if (embedded) embedded.onClose(); else navigate(base); };
    if (dirty) modal.confirm({ title: t('invoices:workflow.page.closeUnsavedConfirm'), okText: t('common:yes'), cancelText: t('common:no'), onOk: leave });
    else leave();
  };
  const continueToReceipt = async () => {
    if (busy || extracting || conflict || !current.current || validateWorkflowInvoiceForApproval(current.current).length) return;
    const session = draftSession.current;
    const capturedRevision = revision.current;
    setBusy(true);
    try {
      const saved = dirty || !current.current.id ? await save() : current.current;
      if (saved && session === draftSession.current && capturedRevision === revision.current) setPurchaseStep(2);
    } catch (cause) { if (session === draftSession.current) error(cause); }
    finally { finishBusy(session); }
  };
  if (loading || !draft) return <Space direction="vertical">
    {loading ? <Spin /> : <Alert type="error" message={t('invoices:workflow.api.draftNotFound')} />}
    {embedded && <Button onClick={close}>{t('common:close')}</Button>}
  </Space>;
  const editable = draft.status === 'DRAFT';
  const filePresent = Boolean(draft.sourceFileName || draft.sourceFiles?.length);
  const showApply = editable && draft.extractionStatus === 'SUCCESS' && draft.extractionSeq !== draft.lastAppliedExtractionSeq;
  const issues = validateWorkflowInvoiceForApproval(draft);
  const persisted = Boolean(draft.id);
  const progressBusy = busy || extracting;
  const triggerUpload = () => fileInput.current?.click();
  const requestExtraction = () => { if (conflict) return; if (filePresent) void extract(); else triggerUpload(); };
  const orderPicker = (
    <InvoiceOrderPicker direction={direction} ids={sourceIds} disabled={!editable || busy} onCandidates={setCandidates} onLoading={setSourcesLoading} onChange={(ids, supplierId) => {
      setSourceIds(ids);
      edit({ ...draft, ...(direction === 'PURCHASE' ? { supplierOrderId: ids[0] ?? null, supplierId: supplierId ?? null } : { orderId: ids[0] ?? null }), manuallyCorrected: true });
    }} />
  );
  const numberFields = direction === 'PURCHASE' && preview && <InvoicePZNumbers orders={preview.orders.filter(order => order.lines.some(line => draft.lines.some(item => item.sourceOrderLineId === line.sourceLineId)))}
    values={pzNumbers} disabled={progressBusy || dirty || conflict}
    onChange={values => { approvalKey.current = null; setPZNumbers(values); }} />;
  if (purchaseFlow && editable) return <>
    <InvoicePurchaseFlow draft={draft} step={purchaseStep} setStep={setPurchaseStep}
      busy={busy} extracting={extracting} conflict={conflict} dirty={dirty} issues={issues}
      showApply={showApply} onApply={confirmApply} onUpload={triggerUpload} onExtract={requestExtraction}
      onSave={() => void handleModalSave()} onContinue={continueToReceipt} onApprove={() => void approve()} onClose={close}
      onPeriod={settlementPeriod => replace({ ...draft, settlementPeriod })} onDownload={downloadFile}
      onDelete={() => modal.confirm({ title: t('invoices:workflow.page.deleteConfirm'), okText: t('common:yes'), cancelText: t('common:no'), onOk: () => commands.remove(draft.id!).then(() => { if (embedded) { embedded.onPersisted(); embedded.onClose(); } else navigate(base); }).catch(error) })}
      onReload={() => modal.confirm({ title: t('invoices:workflow.page.discardConfirm'), okText: t('common:yes'), cancelText: t('common:no'), onOk: () => commands.fetch(draft.id!).then(value => { replace(value); setDirty(false); setConflict(false); setPurchaseStep(1); }).catch(error) })}
      canReceive={Boolean(preview && !previewFailed && !approvalReviewRequired && !preview.receiptCostsIssue && preview.receiptCosts?.length && !issues.length && !receiptCosts.some(row => row.totalCostPLN == null || row.totalCostPLN < 0))}
      orders={orderPicker}
      editor={<WorkflowInvoiceEditor compactReview value={draft} onChange={edit} extractionStatus={draft.extractionStatus}
        extractionError={draft.extractionError} extractionPending={extracting} onRequestExtraction={requestExtraction}
        sourceLineCandidates={candidates} sourceLinesLoading={sourcesLoading} readOnly={progressBusy || conflict} partialCoverage={draft.coverageState === 'PARTIAL'} />}
      receipt={<>
        {numberFields}
        {previewFailed ? <Alert type="warning" message={t('invoices:workflow.page.previewFailed')} action={<Button onClick={() => setPreviewRevision(value => value + 1)}>{t('common:refresh')}</Button>} /> : !preview ? <Spin /> : <>
          {approvalReviewRequired && <Alert type="warning" message={t('invoices:workflow.receiptCost.reviewAgain')} action={<Button disabled={progressBusy || dirty} onClick={() => setApprovalReviewRequired(false)}>{t('invoices:workflow.receiptCost.reviewConfirmed')}</Button>} />}
          <InvoiceReceiptCostReview rows={preview.receiptCosts ?? []} values={receiptCosts} onChange={values => { approvalKey.current = null; setReceiptCosts(values); }} disabled={progressBusy || dirty} issue={preview.receiptCostsIssue} />
          {preview.pendingStockCandidates.some(group => group.candidates.length > 0) && <InvoiceApprovalPreview invoice={draft} preview={preview} selections={pendingSelections} onChange={values => { approvalKey.current = null; setPendingSelections(values); }} disabled={progressBusy || dirty} />}
        </>}
      </>} />
    <input data-testid="invoice-source-input" ref={fileInput} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" hidden onChange={event => { const files = Array.from(event.target.files ?? []); event.target.value = ''; void upload(files); }} />
  </>;
  return <div className="invoice-draft-page"><Space direction="vertical" size={16} style={{ width: '100%' }}>
    <div className="invoice-draft-overview" hidden={Boolean(embedded && editorOpen)}>
    <div className="invoice-draft-topbar">
      <Space size={8}>
        <Button icon={embedded ? undefined : <ArrowLeftOutlined />} disabled={progressBusy} onClick={close}>{t(embedded ? 'common:close' : 'common:back')}</Button>
        <Tag>{t(`invoices:workflow.direction.${direction.toLowerCase()}`)}</Tag>
      </Space>
      {editable && persisted && <Popconfirm okText={t('common:yes')} cancelText={t('common:no')} title={t('invoices:workflow.page.deleteConfirm')} onConfirm={() => { void commands.remove(draft.id!).then(() => { if (embedded) { embedded.onPersisted(); embedded.onClose(); } else navigate(base); }).catch(error); }}><Button danger type="text" disabled={progressBusy}>{t('invoices:workflow.page.deleteDraft')}</Button></Popconfirm>}
    </div>
    {conflict && <Alert type="error" showIcon message={t('invoices:workflow.api.versionConflict')} description={t('invoices:workflow.page.conflictRetained')} action={<Button onClick={() => modal.confirm({ title: t('invoices:workflow.page.discardConfirm'), okText: t('common:yes'), cancelText: t('common:no'), onOk: () => { void commands.fetch(draft.id!).then(value => { replace(value); setDirty(false); setConflict(false); }).catch(error); } })}>{t('common:refresh')}</Button>} />}
    {!persisted ? (
      <div className="invoice-intake" data-testid="invoice-intake">
        <h2 className="invoice-intake-title">{t('invoices:workflow.summary.intakeTitle')}</h2>
        {dirty && <Tag color="gold" data-testid="invoice-intake-unsaved">{t('invoices:workflow.summary.unsaved')}</Tag>}
        <p className="invoice-intake-hint">{t('invoices:workflow.summary.intakeHint')}</p>
        <div className="invoice-intake-actions">
          <Button type="primary" icon={<UploadOutlined />} disabled={progressBusy} onClick={triggerUpload} data-testid="invoice-intake-upload">
            {t('invoices:workflow.summary.uploadFile')}
          </Button>
          <Button icon={<FileAddOutlined />} disabled={progressBusy} onClick={() => setEditorOpen(true)} data-testid="invoice-intake-manual">
            {t(`invoices:workflow.summary.${dirty ? 'reopenDetails' : 'manualEntry'}`)}
          </Button>
        </div>
        {filePresent && <Space wrap style={{ marginTop: 16 }}>
          <Button icon={<UploadOutlined />} disabled={progressBusy} onClick={() => void extract()}>{t('invoices:workflow.actions.requestExtraction')}</Button>
        </Space>}
      </div>
    ) : (
      <InvoiceDraftSummary
        draft={draft}
        editable={editable}
        dirty={dirty}
        busy={busy}
        extracting={extracting}
        conflict={conflict}
        filePresent={filePresent}
        issues={issues}
        showApply={showApply}
        sourceCount={sourceIds.length}
        onEdit={() => setEditorOpen(true)}
        onUpload={triggerUpload}
        onRequestExtraction={requestExtraction}
        onDownloadFile={downloadFile}
        onApply={confirmApply}
      />
    )}
    {!editable && persisted && (
      <Space wrap>
        <Button onClick={() => setEditorOpen(true)} data-testid="invoice-view-details">
          {t('invoices:workflow.summary.viewDetails')}
        </Button>
      </Space>
    )}
    {persisted && editable && direction === 'PURCHASE' && preview && (
      <>
      {approvalReviewRequired && <Alert type="warning" showIcon message={t('invoices:workflow.receiptCost.reviewAgain')}
        description={t('invoices:workflow.receiptCost.resetExplanation')}
        action={<Button disabled={progressBusy || dirty} onClick={() => setApprovalReviewRequired(false)}>{t('invoices:workflow.receiptCost.reviewConfirmed')}</Button>} />}
      <InvoiceReceiptCostReview rows={preview.receiptCosts ?? []} values={receiptCosts}
        onChange={values => { approvalKey.current = null; setReceiptCosts(values); }}
        disabled={progressBusy || dirty} issue={preview.receiptCostsIssue} />
      </>
    )}
    {persisted && (
      <div className="invoice-draft-section">
        <span className="invoice-draft-section-label">{t('invoices:workflow.summary.ordersTitle')}</span>
        <Collapse ghost items={[{
          key: 'review',
          label: t('invoices:workflow.summary.reviewTitle'),
          forceRender: true,
          children: preview
            ? <InvoiceApprovalPreview invoice={draft} preview={preview} selections={pendingSelections} onChange={values => { approvalKey.current = null; setPendingSelections(values); }} disabled={!editable || progressBusy || dirty} />
            : previewFailed
              ? <Alert type="warning" message={t('invoices:workflow.page.previewFailed')} action={<Button onClick={() => setPreviewRevision(value => value + 1)}>{t('common:refresh')}</Button>} />
              : <Spin size="small" />,
        }]} />
      </div>
    )}
    {editable && persisted && (
      <div className="invoice-draft-section">
        {numberFields}
        <Collapse ghost items={[{
          key: 'finalization',
          label: t('invoices:workflow.page.finalization'),
          forceRender: true,
          children: (
            <Space wrap>
              <DatePicker picker="month" aria-label={t('invoices:workflow.page.recognitionMonth')} placeholder={t('invoices:workflow.page.recognitionMonth')} value={draft.settlementPeriod ? dayjs(`${draft.settlementPeriod}-01`) : null} disabled={progressBusy || conflict} onChange={date => replace({ ...draft, settlementPeriod: date?.format('YYYY-MM') ?? null })} />
              <Popconfirm okText={t('common:yes')} cancelText={t('common:no')} title={t('invoices:workflow.page.approveConfirm')} onConfirm={() => void approve()} disabled={progressBusy || conflict}>
                <Button type="primary" loading={busy} disabled={extracting || conflict || approvalReviewRequired}>{t(`invoices:workflow.page.${direction === 'PURCHASE' ? 'approvePurchase' : 'approveSales'}`)}</Button>
              </Popconfirm>
            </Space>
          ),
        }]} />
      </div>
    )}
    {approval && <Card title={t('invoices:workflow.page.approved')}><Space direction="vertical">
      {approval.pzDocuments.map(pz => <Button key={pz.id} onClick={() => navigate(`/app/pz/${pz.id}`)}>{pz.pzNumber}</Button>)}
      {approval.orders.map(order => <Typography.Text key={order.id}>{t(`invoices:workflow.page.coverage.${order.coverageState.toLowerCase()}`)}</Typography.Text>)}
    </Space></Card>}
    </div>
    <InvoiceEditorModal
      inline={Boolean(embedded)}
      open={editorOpen}
      title={draft.originalDocumentNumber || t(`invoices:workflow.page.${direction === 'PURCHASE' ? 'title' : 'salesTitle'}`)}
      dirty={dirty}
      saving={busy}
      saveDisabled={extracting || conflict}
      onSave={() => void handleModalSave()}
      onClose={() => setEditorOpen(false)}
      readOnly={!editable}
    >
      <Collapse ghost defaultActiveKey={['orders']} className="invoice-draft-modal-orders" items={[{
        key: 'orders',
        label: `${t('invoices:workflow.page.sourceOrders')}${sourceIds.length > 0 ? ` · ${sourceIds.length}` : ''}`,
        forceRender: true,
        children: orderPicker,
      }]} />
      <WorkflowInvoiceEditor value={draft} onChange={edit} extractionStatus={draft.extractionStatus} extractionError={draft.extractionError} extractionModel={draft.extractionModel} extractionPending={extracting} extractionStale={dirty && draft.extractionStatus === 'SUCCESS'} onRequestExtraction={requestExtraction} sourceLineCandidates={candidates} sourceLinesLoading={sourcesLoading} readOnly={!editable || busy || conflict} partialCoverage={draft.coverageState === 'PARTIAL'} frozenOrder={draft.status === 'APPROVED'} />
    </InvoiceEditorModal>
    <input data-testid="invoice-source-input" ref={fileInput} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={event => { const files = Array.from(event.target.files ?? []); event.target.value = ''; void upload(files); }} />
  </Space></div>;
}
